import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline, SectionHeading } from "@/components/Primitives";
import { InnerHero, CTABand } from "@/components/Sections";
import { IMG, STAY_IMG } from "@/data/site";

const VALUES = [
  { title: "30 Years of Expertise", text: "Decades of experience across leisure travel, corporate travel, destination management and MICE." },
  { title: "Strong Hotel Network", text: "Long-standing relationships with South Indian hotels and resorts support excellent service and competitive value." },
  { title: "In-house Operations", text: "Dedicated product, customer care, logistics and transport teams maintain control over every journey." },
  { title: "Multilingual Support", text: "A network of guides and drivers fluent in English, Spanish, French, Arabic and German." },
];

export default function About() {
  return (
    <>
      <InnerHero overline="About Travel Bug India" title="The team behind the journeys" subtitle="A Kochi-headquartered destination management company creating tailored journeys across South India." image={STAY_IMG.bruntonBoatyard} />

      <section className="bg-ivory py-24 md:py-32" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Overline className="text-gold">Our Story</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] text-forest">South India expertise, built over decades</h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-navy/70">Travel Bug India is an inbound tour and travel company headquartered in Kochi, with offices in Chennai, Bangalore and Dubai. With 30 years of tourism experience, the team provides holiday planning, itinerary design, hotel booking, destination management, ground handling, logistics and transport services.</p>
            <p className="mt-4 text-navy/60 font-light leading-relaxed">We specialise in FITs, group tours, Ayurveda and wellness retreats, heritage and cultural experiences, special-interest holidays, luxury travel, eco-tourism, MICE and medical tourism across Kerala, Tamil Nadu, Pondicherry and Karnataka.</p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-forest font-semibold text-sm">Meet our local team <ArrowRight size={16} /></Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.tea1} alt="South Indian hill country" className="rounded-lg h-64 w-full object-cover" />
              <img src={IMG.houseboat} alt="Kerala backwaters" className="rounded-lg h-64 w-full object-cover mt-8" />
              <img src={STAY_IMG.spiceVillage} alt="South Indian wellness retreat" className="rounded-lg h-64 w-full object-cover" />
              <img src={STAY_IMG.marariBeach} alt="Kerala beach resort" className="rounded-lg h-64 w-full object-cover mt-8" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand/30 py-24 md:py-32" data-testid="about-values">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="Why Travel Bug India" title="Local control, international care" /></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {VALUES.map((value, index) => <Reveal key={value.title} delay={(index % 2) * 0.08}><div className="h-full rounded-lg border border-sand bg-ivory p-8"><h3 className="font-serif text-2xl text-forest">{value.title}</h3><p className="mt-3 text-navy/65 font-light leading-relaxed">{value.text}</p></div></Reveal>)}
          </div>
        </div>
      </section>
      <CTABand overline="Work with us" title="Let's plan something meaningful" primary={{ label: "Plan Your Journey", to: "/contact" }} secondary={{ label: "See Our Experiences", to: "/experiences" }} />
    </>
  );
}
